/* Локальные сведения о выбранном файле для рамки и героя хвоста: имя, размер, длительность, волна или первый кадр.
   Читаются в браузере из самого файла; после перезагрузки (файл восстановлен по id загрузки) их нет — и рамка их не рисует. */
import { readDurationMs } from '../../clone/sample-file';

export interface SourceMeta {
  name: string;
  size: number;
  kind: 'audio' | 'video' | 'photo';
  durationMs?: number;
  peaks?: number[];
  /** Первый кадр ролика или превью фото (blob:) */
  poster?: string;
  ratio?: number;
}
const BARS = 48;
async function audioPeaks(file: File): Promise<number[] | undefined> {
  if (file.size > 25 * 1048576 || typeof OfflineAudioContext === 'undefined') return undefined;
  const context = new OfflineAudioContext(1, 1, 8000);
  const decoded = await context.decodeAudioData(await file.arrayBuffer());
  const data = decoded.getChannelData(0);
  const step = Math.max(1, Math.floor(data.length / BARS));
  const peaks: number[] = [];
  for (let bar = 0; bar < BARS; bar++) {
    let max = 0;
    for (let index = bar * step; index < Math.min(data.length, (bar + 1) * step); index += 16) max = Math.max(max, Math.abs(data[index]));
    peaks.push(max);
  }
  const top = Math.max(...peaks, 0.0001);
  return peaks.map(peak => Math.max(0.12, peak / top));
}
function videoPoster(file: File): Promise<{ poster?: string; ratio?: number; durationMs?: number }> {
  return new Promise(resolve => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    const timer = window.setTimeout(() => finish({}), 8000);
    const finish = (value: { poster?: string; ratio?: number; durationMs?: number }) => {
      window.clearTimeout(timer);
      video.removeAttribute('src');
      URL.revokeObjectURL(url);
      resolve(value);
    };
    video.muted = true;
    video.preload = 'metadata';
    video.playsInline = true;
    video.onloadedmetadata = () => {
      try {
        video.currentTime = Math.min(0.1, video.duration / 2 || 0);
      } catch {
        finish({});
      }
    };
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      let poster: string | undefined;
      try {
        canvas.getContext('2d')?.drawImage(video, 0, 0);
        poster = canvas.toDataURL('image/jpeg', 0.7);
      } catch {
        poster = undefined;
      }
      finish({
        poster,
        ratio: video.videoWidth && video.videoHeight ? video.videoWidth / video.videoHeight : undefined,
        durationMs: Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : undefined,
      });
    };
    video.onerror = () => finish({});
    video.src = url;
  });
}
export async function readSourceMeta(file: File, kind: SourceMeta['kind'], signal?: AbortSignal): Promise<SourceMeta> {
  const meta: SourceMeta = { name: file.name, size: file.size, kind };
  if (kind === 'audio') {
    meta.durationMs = await readDurationMs(file, signal).catch(() => undefined);
    meta.peaks = await audioPeaks(file).catch(() => undefined);
  } else if (kind === 'video') {
    Object.assign(meta, await videoPoster(file));
    if (!meta.durationMs) meta.durationMs = await readDurationMs(file, signal).catch(() => undefined);
  }
  return meta;
}
/** Ровный ряд столбиков, пока волна не прочитана (оформление пустой волны, не данные) */
export const flatPeaks = Array.from({ length: BARS }, (_, index) => 0.25 + 0.2 * Math.abs(Math.sin(index * 0.7)));
